/**
 * VideoEditor - Modal for creating Reels/Shorts from campaign images
 * Sends request to backend FFmpeg pipeline, provides download
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Download, Loader2, Play, Clock, Sparkles, Film, ZoomIn, ZoomOut,
  MoveHorizontal, MoveVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';

const EFFECTS = [
  { key: 'zoom_in', label: 'Zoom In', icon: ZoomIn },
  { key: 'zoom_out', label: 'Zoom Out', icon: ZoomOut },
  { key: 'pan_left', label: 'Pan Left', icon: MoveHorizontal },
  { key: 'pan_right', label: 'Pan Right', icon: MoveHorizontal },
  { key: 'pan_up', label: 'Pan Up', icon: MoveVertical },
];

const DURATIONS = [5, 8, 10, 15];

const TEXT_STYLES = [
  { key: 'dark', label: 'Ciemne tło', preview: 'bg-black/50 text-white' },
  { key: 'light', label: 'Jasne tło', preview: 'bg-white/50 text-black' },
  { key: 'none', label: 'Bez tła', preview: 'text-white' },
];

export default function VideoEditor({ imageUrl, cardType, cardContent, onClose }) {
  const [headline, setHeadline] = useState(
    cardContent?.title?.slice(0, 50) || ''
  );
  const [subtitle, setSubtitle] = useState('');
  const [effect, setEffect] = useState('zoom_in');
  const [duration, setDuration] = useState(8);
  const [textBg, setTextBg] = useState('dark');
  const [generating, setGenerating] = useState(false);
  const [videoResult, setVideoResult] = useState(null);

  const token = localStorage.getItem('token');

  const [showPlayer, setShowPlayer] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    setVideoResult(null);
    setShowPlayer(false);
    try {
      const res = await axios.post(`${API}/booster/video/generate`, {
        image_url: imageUrl,
        headline,
        subtitle,
        effect,
        duration,
        text_color: textBg === 'light' ? 'black' : 'white',
        text_bg: textBg,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setVideoResult(res.data);
      toast.success('Video wygenerowane!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd generowania wideo');
    } finally {
      setGenerating(false);
    }
  };

  const baseUrl = API.replace('/api', '');

  const handleDownload = () => {
    if (videoResult?.download_url) {
      const a = document.createElement('a');
      a.href = `${baseUrl}${videoResult.download_url}`;
      a.download = `apisai-reel.mp4`;
      a.click();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" data-testid="video-editor-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--apisai-border)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#F7931E' }}>
              <Film className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ color: 'var(--apisai-text)' }}>Stwórz Video</h3>
              <p className="text-xs" style={{ color: 'var(--apisai-text-muted)' }}>Reel / Short · Format 9:16</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-zinc-100">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Preview column */}
            <div>
              <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: '9/16', maxHeight: '400px' }}>
                <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                {/* Text preview overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  {headline && (
                    <div className={`text-center mb-1 px-3 py-1.5 rounded-lg inline-block w-full ${
                      textBg === 'dark' ? 'bg-black/55 text-white' : textBg === 'light' ? 'bg-white/55 text-black' : 'text-white'
                    }`}>
                      <p className="font-bold text-sm">{headline}</p>
                    </div>
                  )}
                  {subtitle && (
                    <div className={`text-center px-3 py-1 rounded-lg inline-block w-full ${
                      textBg === 'dark' ? 'bg-black/55 text-white' : textBg === 'light' ? 'bg-white/55 text-black' : 'text-white'
                    }`}>
                      <p className="text-xs">{subtitle}</p>
                    </div>
                  )}
                </div>
                {/* Effect indicator */}
                <div className="absolute top-3 left-3 px-2 py-1 rounded-lg bg-black/40 text-white text-[10px] font-bold flex items-center gap-1">
                  <Play className="w-2.5 h-2.5" /> {effect.replace('_', ' ')} · {duration}s
                </div>
                {/* Format badge */}
                <div className="absolute top-3 right-3 px-2 py-1 rounded-lg bg-orange-500 text-white text-[10px] font-bold">
                  9:16
                </div>
              </div>
            </div>

            {/* Settings column */}
            <div className="space-y-4">
              {/* Headline */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--apisai-text-secondary)' }}>
                  Nagłówek
                </label>
                <input
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  maxLength={60}
                  placeholder="np. Szczenięta Owczarka"
                  className="w-full rounded-xl border px-3 py-2 text-sm"
                  style={{ borderColor: 'var(--apisai-border)' }}
                  data-testid="video-headline"
                />
              </div>

              {/* Subtitle */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--apisai-text-secondary)' }}>
                  Podtytuł (opcjonalnie)
                </label>
                <input
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  maxLength={80}
                  placeholder="np. Rezerwacja otwarta"
                  className="w-full rounded-xl border px-3 py-2 text-sm"
                  style={{ borderColor: 'var(--apisai-border)' }}
                  data-testid="video-subtitle"
                />
              </div>

              {/* Effect */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--apisai-text-secondary)' }}>
                  Efekt ruchu
                </label>
                <div className="grid grid-cols-3 gap-1.5" data-testid="video-effects">
                  {EFFECTS.map(e => {
                    const Icon = e.icon;
                    return (
                      <button
                        key={e.key}
                        onClick={() => setEffect(e.key)}
                        className="flex flex-col items-center gap-1 px-2 py-2 rounded-xl text-xs font-medium border transition-all"
                        style={{
                          background: effect === e.key ? '#FEF3E2' : 'white',
                          borderColor: effect === e.key ? '#F7931E' : 'var(--apisai-border)',
                          color: effect === e.key ? '#F7931E' : 'var(--apisai-text-secondary)',
                        }}
                        data-testid={`effect-${e.key}`}
                      >
                        <Icon className="w-4 h-4" />
                        {e.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Duration */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--apisai-text-secondary)' }}>
                  Czas trwania
                </label>
                <div className="flex gap-2" data-testid="video-durations">
                  {DURATIONS.map(d => (
                    <button
                      key={d}
                      onClick={() => setDuration(d)}
                      className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold border transition-all"
                      style={{
                        background: duration === d ? '#F7931E' : 'white',
                        color: duration === d ? 'white' : 'var(--apisai-text-secondary)',
                        borderColor: duration === d ? '#F7931E' : 'var(--apisai-border)',
                      }}
                      data-testid={`duration-${d}`}
                    >
                      <Clock className="w-3 h-3" /> {d}s
                    </button>
                  ))}
                </div>
              </div>

              {/* Text style */}
              <div>
                <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--apisai-text-secondary)' }}>
                  Styl tekstu
                </label>
                <div className="flex gap-2" data-testid="video-text-styles">
                  {TEXT_STYLES.map(s => (
                    <button
                      key={s.key}
                      onClick={() => setTextBg(s.key)}
                      className="px-3 py-2 rounded-xl text-xs font-medium border transition-all"
                      style={{
                        background: textBg === s.key ? '#FEF3E2' : 'white',
                        borderColor: textBg === s.key ? '#F7931E' : 'var(--apisai-border)',
                        color: textBg === s.key ? '#F7931E' : 'var(--apisai-text-secondary)',
                      }}
                      data-testid={`textstyle-${s.key}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate button */}
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full rounded-xl h-11 font-semibold text-white"
                style={{ background: '#F7931E' }}
                data-testid="generate-video-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generuję video...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Generuj Video</>
                )}
              </Button>

              {/* Result with thumbnail + player */}
              {videoResult && (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl overflow-hidden"
                  style={{ border: '2px solid #10B98140' }}
                  data-testid="video-result"
                >
                  {/* Thumbnail / Player */}
                  <div className="relative" style={{ aspectRatio: '9/16', maxHeight: '240px' }}>
                    {showPlayer ? (
                      <video
                        src={`${baseUrl}${videoResult.download_url}`}
                        controls
                        autoPlay
                        className="w-full h-full object-contain bg-black"
                        data-testid="video-player"
                      />
                    ) : (
                      <>
                        <img
                          src={`${baseUrl}${videoResult.thumbnail_url}`}
                          alt="Video thumbnail"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = imageUrl; }}
                        />
                        <button
                          onClick={() => setShowPlayer(true)}
                          className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors"
                          data-testid="play-video-btn"
                        >
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                            <Play className="w-5 h-5 text-orange-500 ml-0.5" />
                          </div>
                        </button>
                      </>
                    )}
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-lg bg-black/50 text-white text-[10px] font-bold">
                      {videoResult.duration}s · {videoResult.file_size_kb}KB
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="p-3 flex gap-2" style={{ background: '#F0FDF4' }}>
                    <Button
                      onClick={() => setShowPlayer(!showPlayer)}
                      variant="outline"
                      className="flex-1 rounded-xl h-9 text-xs font-medium"
                      data-testid="toggle-player-btn"
                    >
                      <Play className="w-3.5 h-3.5 mr-1" /> {showPlayer ? 'Miniatura' : 'Odtwórz'}
                    </Button>
                    <Button
                      onClick={handleDownload}
                      className="flex-1 rounded-xl h-9 text-xs font-medium text-white"
                      style={{ background: '#10B981' }}
                      data-testid="download-video-btn"
                    >
                      <Download className="w-3.5 h-3.5 mr-1" /> Pobierz MP4
                    </Button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
